import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Paper,
  Divider,
  Avatar,
  Stack,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ShoppingCart as MerchantCenterIcon,
  Check as CheckIcon,
  Verified as VerifiedIcon,
  Language as WebsiteIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { brandService } from '../../services/brandService';
import api from '../../config/api';

const GoogleMerchantCenterSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountBrandMapping, setAccountBrandMapping] = useState({}); // Maps accountId -> brandId
  const [creating, setCreating] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registrationResults, setRegistrationResults] = useState({});
  const [showRegistrationButton, setShowRegistrationButton] = useState(false);

  // Extract OAuth data from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const tempId = urlParams.get('temp_id');
  const brandId = urlParams.get('brand_id');

  useEffect(() => {
    if (!tempId || !brandId) {
      setError('Invalid OAuth state. Please try connecting again.');
      return;
    }

    // OAuth flow provides default brand ID for mapping

    fetchMerchantCenterAccounts();
    fetchBrands();
  }, [tempId, brandId]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
      // Don't set error for brands as accounts are more critical
    }
  };

  const fetchMerchantCenterAccounts = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/api/connectors/google/merchant-center/accounts', {
        params: {
          temp_id: tempId
        }
      });

      if (response.data.success) {
        setAccounts(response.data.accounts || []);
        setShowRegistrationButton(false); // Reset registration button if successful
      } else {
        const errorMessage = response.data.message || 'Failed to fetch Merchant Center accounts';
        setError(errorMessage);
        // Check if error suggests developer registration is needed
        if (errorMessage.toLowerCase().includes('403') || 
            errorMessage.toLowerCase().includes('developer') || 
            errorMessage.toLowerCase().includes('access denied') ||
            errorMessage.toLowerCase().includes('insufficient permissions')) {
          setShowRegistrationButton(true);
        }
      }

    } catch (err) {
      console.error('Error fetching Merchant Center accounts:', err);
      let errorMessage = '';
      let shouldShowRegistration = false;
      
      if (err.response?.status === 403) {
        errorMessage = 'Access denied. Please ensure your Google account has access to Google Merchant Center.';
        shouldShowRegistration = true;
      } else if (err.response?.status === 404) {
        errorMessage = 'No Merchant Center accounts found. Please ensure you have access to at least one Merchant Center account.';
        shouldShowRegistration = true;
      } else {
        errorMessage = err.response?.data?.message || 'Failed to fetch Merchant Center accounts. Please try again.';
        // Check if error suggests developer registration is needed
        if (errorMessage.toLowerCase().includes('403') || 
            errorMessage.toLowerCase().includes('developer') || 
            errorMessage.toLowerCase().includes('access') ||
            errorMessage.toLowerCase().includes('permission') ||
            err.response?.status === 403) {
          shouldShowRegistration = true;
        }
      }
      
      setError(errorMessage);
      setShowRegistrationButton(shouldShowRegistration);
    } finally {
      setLoading(false);
    }
  };

  const handleDeveloperRegistration = async () => {
    setRegistering(true);
    setError('');
    const newResults = {};

    try {
      for (const account of accounts) {
        try {
          const payload = {
            temp_id: tempId,
            account_id: account.accountId
          };

          const response = await api.post('/api/connectors/google/merchant-center/register-developer', payload);

          if (response.data.success) {
            newResults[account.accountId] = {
              success: true,
              message: response.data.message,
              alreadyRegistered: response.data.alreadyRegistered
            };
          } else {
            newResults[account.accountId] = {
              success: false,
              message: response.data.message || 'Registration failed'
            };
          }
        } catch (accountError) {
          console.error(`Registration failed for account ${account.accountId}:`, accountError);
          newResults[account.accountId] = {
            success: false,
            message: accountError.response?.data?.message || 'Registration failed'
          };
        }
      }

      setRegistrationResults(newResults);
      
      // Check if any registrations were successful
      const successCount = Object.values(newResults).filter(r => r.success).length;
      if (successCount > 0) {
        // Wait a moment then retry fetching accounts
        setTimeout(() => {
          fetchMerchantCenterAccounts();
        }, 2000);
      }

    } catch (err) {
      console.error('Developer registration error:', err);
      setError('Failed to register developer access. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  const handleAccountSelection = (accountId, checked) => {
    if (checked) {
      const account = accounts.find(a => a.accountId === accountId);
      setSelectedAccounts([...selectedAccounts, account]);
      // For already-connected accounts, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth URL params.
      const defaultBrand = account?.alreadyConnected && account?.connectedBrandId
        ? String(account.connectedBrandId)
        : brandId;
      setAccountBrandMapping(prev => ({
        ...prev,
        [accountId]: defaultBrand
      }));
    } else {
      setSelectedAccounts(selectedAccounts.filter(a => a.accountId !== accountId));
      // Remove brand mapping when account is deselected
      setAccountBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[accountId];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (accountId, newBrandId) => {
    setAccountBrandMapping(prev => ({
      ...prev,
      [accountId]: newBrandId
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedAccounts.length === 0) {
      setError('Please select at least one Merchant Center account to connect');
      return;
    }

    // Validate that all selected accounts have brands assigned
    const accountsWithoutBrands = selectedAccounts.filter(account => 
      !accountBrandMapping[account.accountId]
    );
    if (accountsWithoutBrands.length > 0) {
      setError(`Please select a brand for all selected accounts. Missing brands for: ${accountsWithoutBrands.map(a => a.name).join(', ')}`);
      return;
    }

    setCreating(true);
    setError('');

    try {
      const createdConnectors = [];

      for (const account of selectedAccounts) {
        const selectedBrandId = accountBrandMapping[account.accountId] || brandId;
        const payload = {
          temp_id: tempId,
          brand_id: selectedBrandId,
          account_id: account.accountId,
          account_name: account.name,
          website_url: account.websiteUrl,
          country: account.country,
          currency: account.currency
        };

        const response = await api.post('/api/connectors/google/merchant-center/create', payload);

        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${account.name}: ${response.data.message}`);
        }
      }

      // Redirect to connectors page with success message
      const uniqueBrands = [...new Set(selectedAccounts.map(account => 
        getBrandName(accountBrandMapping[account.accountId])
      ))];
      
      navigate('/connectors', {
        state: {
          message: `Successfully created ${createdConnectors.length} Google Merchant Center connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}`,
          severity: 'success'
        }
      });

    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  // Legacy single connector creation (keeping for backward compatibility)
  const handleCreateConnector = async (account) => {
    try {
      setCreating(account.accountId);
      setError('');

      const payload = {
        temp_id: tempId,
        brand_id: brandId,
        account_id: account.accountId,
        account_name: account.name,
        website_url: account.websiteUrl,
        country: account.country,
        currency: account.currency
      };

      const response = await api.post('/api/connectors/google/merchant-center/create', payload);

      if (response.data.success) {
        // Show success message
        const successMessage = `Google Merchant Center connector created successfully for ${account.name}`;
        
        // Redirect to connectors page with success message
        navigate('/connectors', { 
          state: { 
            message: successMessage,
            severity: 'success'
          }
        });
      } else {
        setError(response.data.message || 'Failed to create connector');
      }

    } catch (err) {
      console.error('Error creating connector:', err);
      setError(err.response?.data?.message || 'Failed to create connector. Please try again.');
    } finally {
      setCreating(null);
    }
  };

  const handleCancel = () => {
    navigate('/connectors');
  };

  // Helper function to get brand name by ID
  const getBrandName = (brandId) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(brandId));
    return brand ? brand.name : 'Unknown Brand';
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading your Google Merchant Center accounts...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #34A853 0%, #137333 100%)', color: 'white' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
            <MerchantCenterIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Google Merchant Center
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Select Merchant Center accounts to connect
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError('')}
          action={
            showRegistrationButton && (
              <Button
                color="inherit"
                size="small"
                onClick={handleDeveloperRegistration}
                disabled={registering}
                startIcon={registering ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {registering ? 'Registering...' : 'Register Developer Access'}
              </Button>
            )
          }
        >
          {error}
          {showRegistrationButton && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              This error might be resolved by registering developer access for your Merchant Center accounts.
            </Typography>
          )}
        </Alert>
      )}

      {/* Registration Results */}
      {Object.keys(registrationResults).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Developer Registration Results
            </Typography>
            {Object.entries(registrationResults).map(([accountId, result]) => {
              const account = accounts.find(a => a.accountId === accountId);
              return (
                <Alert 
                  key={accountId}
                  severity={result.success ? 'success' : 'error'}
                  sx={{ mb: 1 }}
                >
                  <strong>{account?.name || accountId}:</strong> {result.message}
                </Alert>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Choose Your Merchant Center Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We found {accounts.length} Merchant Center account{accounts.length !== 1 ? 's' : ''} associated with your Google account.
            Select one or more accounts you want to connect to import your product data, inventory information, and performance metrics.
            Each selected account will create a separate connector.
          </Typography>
        </CardContent>
      </Card>

      {/* Accounts List */}
      {accounts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <MerchantCenterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Merchant Center Accounts Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We couldn't find any Google Merchant Center accounts associated with your account.
              Make sure you have access to at least one Merchant Center account.
            </Typography>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ mr: 2 }}
            >
              Go Back
            </Button>
            {showRegistrationButton && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDeveloperRegistration}
                disabled={registering}
                startIcon={registering ? <CircularProgress size={16} /> : null}
                sx={{ mr: 2 }}
              >
                {registering ? 'Registering...' : 'Register Developer Access'}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={fetchMerchantCenterAccounts}
              startIcon={loading ? <CircularProgress size={16} /> : null}
              disabled={loading}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {accounts.map((account) => (
            <Grid item xs={12} md={6} key={account.accountId}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAccounts.some(a => a.accountId === account.accountId)}
                        onChange={(e) => handleAccountSelection(account.accountId, e.target.checked)}
                        disabled={creating}
                        color="success"
                      />
                    }
                    label={
                      <Box sx={{ width: '100%' }}>
                        {/* Account Header */}
                        <Box display="flex" alignItems="start" justifyContent="space-between" mb={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ bgcolor: '#34A853' }}>
                              <MerchantCenterIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" component="div">
                                {account.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {account.accountId}
                              </Typography>
                              {account.alreadyConnected && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={account.connectedBrandName
                                      ? `Connected · ${account.connectedBrandName}`
                                      : 'Already connected'}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                          {account.verified && (
                            <Chip
                              icon={<VerifiedIcon />}
                              label="Verified"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Account Details */}
                        <Stack spacing={1.5}>
                          {account.websiteUrl && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <WebsiteIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {account.websiteUrl}
                              </Typography>
                            </Box>
                          )}
                          
                          {account.country && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {typeof account.country === 'string' ? account.country : account.country?.name || account.country?.code || 'Unknown'}
                              </Typography>
                            </Box>
                          )}

                          {account.currency && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <BusinessIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                Currency: {typeof account.currency === 'string' ? account.currency : account.currency?.code || 'Unknown'}
                              </Typography>
                            </Box>
                          )}

                          {account.timeZone && (
                            <Typography variant="caption" color="text.secondary">
                              Timezone: {typeof account.timeZone === 'string' ? account.timeZone : account.timeZone?.id || 'Unknown'}
                            </Typography>
                          )}
                        </Stack>

                        {/* Brand Selection - Only show if account is selected */}
                        {selectedAccounts.some(a => a.accountId === account.accountId) && (
                          <Box sx={{ mt: 2 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel id={`brand-select-${account.accountId}`}>Assign to Brand</InputLabel>
                              <Select
                                labelId={`brand-select-${account.accountId}`}
                                value={accountBrandMapping[account.accountId] || ''}
                                label="Assign to Brand"
                                onChange={(e) => handleBrandChange(account.accountId, e.target.value)}
                                disabled={creating}
                                sx={{ borderRadius: 1 }}
                              >
                                {brands.map(brand => (
                                  <MenuItem key={brand.id} value={brand.id}>
                                    {brand.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Footer Actions */}
      {accounts.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedAccounts.length} account(s) selected
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateConnectors}
              disabled={selectedAccounts.length === 0 || creating}
              startIcon={creating ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{
                background: 'linear-gradient(45deg, #34A853 30%, #137333 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #2d8a47 30%, #0f5d2a 90%)'
                }
              }}
            >
              {creating ? 'Creating...' : `Create ${selectedAccounts.length} Connector(s)`}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GoogleMerchantCenterSelection;